// フォーム送信時のフロント側エラー制御
// 1. 文字数制限（data-char-limit）の超過を送信前に検出し、送信を止めてエラーを表示する
//    必須項目（.c-field-footer[data-required-fields]）の未入力も送信前に検出する
// 2. 送信を止めたときと、サーバー側のエラーで画面が再表示されたときに、最初のエラー箇所へスクロールする
//    （既存の入力チェック parsley が止めた場合も対象）
import { FIELD_SELECTOR, ERROR_CLASS, charLimitOf, isOverLimit, isVisible, childOfFooter } from '../lib/char_limit';

// 独自クラスにする。parsley は送信時に自分が管理する欄の parsley-error / parsley-success を付け直すため、
// parsley-error を使うと、こちらが付けた直後に外されてしまう。見た目は CSS で parsley-error と揃えている。
const INVALID_FIELD_CLASS = 'is-input-error';
const ERROR_SELECTOR = '.parsley-error, .is-input-error, .c-field-footer .' + ERROR_CLASS;

const errorElementFor = function(field) {
  return childOfFooter(field, ERROR_CLASS, 'first');
};

const setCharLimitError = function(field, message) {
  const errorElement = errorElementFor(field);
  if (errorElement.textContent !== message) errorElement.textContent = message;
  field.classList.toggle(INVALID_FIELD_CLASS, message !== '');
};

// 超過している入力欄にエラーを表示し、1件でもあれば true を返す（非表示の欄は対象外）
const markOverLimitFields = function(form) {
  let found = false;
  form.querySelectorAll(FIELD_SELECTOR).forEach(function(field) {
    if (!isVisible(field)) return;

    const over = isOverLimit(field);
    setCharLimitError(field, over ? charLimitOf(field) + '文字以内で入力してください' : '');
    if (over) found = true;
  });
  return found;
};

// ---- 必須チェック ----
// エラー表示行（ApplicationHelper#field_footer）が、対象の入力欄の name（data-required-fields）とメッセージを持っている。
// ブラウザ標準の必須チェック（吹き出し）より先にここで止め、他の画面と同じ表示（項目の下にエラー・赤背景・スクロール）にする。
const REQUIRED_FOOTER_SELECTOR = '.c-field-footer[data-required-fields]';

const requiredNamesOf = function(footer) {
  return footer.dataset.requiredFields.split(/\s+/).filter(Boolean);
};

const controlsNamed = function(form, name) {
  return Array.prototype.slice.call(form.querySelectorAll('[name="' + name + '"]'));
};

const hasValue = function(control) {
  return control.value != null && String(control.value).trim() !== '';
};

// name ごとに、値の入った欄が1つ以上あれば満たしている（勤務地のようにモーダルで選ぶ項目は hidden が選んだ数だけ並ぶ）
const isRequiredSatisfied = function(form, footer) {
  return requiredNamesOf(footer).every(function(name) {
    return controlsNamed(form, name).some(hasValue);
  });
};

const setRequiredError = function(form, footer, show) {
  const errorElement = footer.querySelector('.' + ERROR_CLASS);
  const message = show ? footer.dataset.requiredMessage : '';
  if (errorElement && errorElement.textContent !== message) errorElement.textContent = message;

  // 未入力の欄だけ赤くする（hidden は見えないので対象外）
  requiredNamesOf(footer).forEach(function(name) {
    controlsNamed(form, name).forEach(function(control) {
      if (control.type === 'hidden') return;
      control.classList.toggle(INVALID_FIELD_CLASS, show && !hasValue(control));
    });
  });
};

// 未入力の必須項目にエラーを表示し、1件でもあれば true を返す（非表示の項目は対象外）
const markMissingRequired = function(form) {
  let found = false;
  form.querySelectorAll(REQUIRED_FOOTER_SELECTOR).forEach(function(footer) {
    if (!isVisible(footer)) return;

    const missing = !isRequiredSatisfied(form, footer);
    setRequiredError(form, footer, missing);
    if (missing) found = true;
  });
  return found;
};

// エラー表示中の必須項目だけを判定し直す（入力されたら消す。職種の片方だけ選んだ場合などは赤い欄を更新する）
const refreshRequiredErrors = function(form) {
  form.querySelectorAll(REQUIRED_FOOTER_SELECTOR).forEach(function(footer) {
    const errorElement = footer.querySelector('.' + ERROR_CLASS);
    if (!errorElement || errorElement.textContent.trim() === '') return;

    setRequiredError(form, footer, !isRequiredSatisfied(form, footer));
  });
};

const firstErrorIn = function(root) {
  const candidates = Array.prototype.slice.call(root.querySelectorAll(ERROR_SELECTOR));
  for (let i = 0; i < candidates.length; i++) {
    const element = candidates[i];
    const isMessage = element.classList.contains(ERROR_CLASS);
    if (isVisible(element) && (!isMessage || element.textContent.trim() !== '')) return element;
  }
  return null;
};

// rails-ujs は送信時に送信ボタンを無効化する（data-disable-with）。こちらで送信を止めると無効のまま残り、
// 以後クリックしても送信イベントが起きなくなるため、押せる状態に戻す。
// 無効化は送信の十数ミリ秒後に行われるため、止めた直後のしばらくの間は監視して戻し続ける。
// 在籍中のときの退職年月のように、意図して無効にしている入力欄には触れないよう、送信ボタンだけを対象にする。
const SUBMIT_BUTTON_SELECTOR = 'input[type="submit"], button[type="submit"], button:not([type])';
const RESTORE_WATCH_MS = 1000;

const enableSubmitButtons = function(form) {
  form.querySelectorAll(SUBMIT_BUTTON_SELECTOR).forEach(function(button) {
    button.removeAttribute('disabled');
  });
};

const keepSubmitButtonsEnabled = function(form) {
  enableSubmitButtons(form);

  const observer = new MutationObserver(function() { enableSubmitButtons(form); });
  observer.observe(form, { subtree: true, attributes: true, attributeFilter: ['disabled'] });
  setTimeout(function() { observer.disconnect(); }, RESTORE_WATCH_MS);
};

const scrollToFirstError = function(root) {
  const target = firstErrorIn(root);
  if (!target) return;

  // 項目名ごと見えるように、項目のまとまりを基準にする
  const block = target.closest('fieldset, .c-field, dl') || target;
  block.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

// 修正されたらエラー表示を消す
document.addEventListener('input', function(event) {
  const field = event.target;
  if (!field.matches || !field.matches(FIELD_SELECTOR)) return;
  if (!isOverLimit(field)) setCharLimitError(field, '');
});

// ブラウザ標準の必須チェック（required）で送信が止まると submit イベントが発生しないため、
// クリックの時点でも文字数を確認してエラーを表示しておく（送信を止めるのは下の submit 側で行う）
document.addEventListener('click', function(event) {
  if (!event.target.closest) return;

  const button = event.target.closest('input[type="submit"], button[type="submit"], button:not([type])');
  const form = button && (button.form || button.closest('form'));
  if (!form) return;

  markOverLimitFields(form);

  // 必須の未入力はここで止める。送信まで進むとブラウザ標準の吹き出しで止まり、エラー表示もスクロールも出ないため
  if (markMissingRequired(form)) {
    event.preventDefault();
    scrollToFirstError(form);
  }
}, true);

// 選択・入力されたら必須エラーを判定し直す
document.addEventListener('change', function(event) {
  const form = event.target.form || (event.target.closest && event.target.closest('form'));
  if (form) refreshRequiredErrors(form);
});

document.addEventListener('submit', function(event) {
  const form = event.target;
  const overLimit = markOverLimitFields(form);
  const missingRequired = markMissingRequired(form);
  if (overLimit || missingRequired) event.preventDefault();

  // parsley など他の入力チェックが止めた場合も拾うため、送信処理が終わってから判定する
  setTimeout(function() {
    if (!event.defaultPrevented) return;

    keepSubmitButtonsEnabled(form);
    scrollToFirstError(form);
  }, 0);
}, true);

document.addEventListener('DOMContentLoaded', function() {
  // 送信が途中で止まって無効のまま残った送信ボタンを戻す（無効なボタンはクリックしても何も起きないため）
  document.querySelectorAll('[data-disable-with][disabled]').forEach(function(button) {
    button.removeAttribute('disabled');
  });

  // 勤務地・雇用形態はモーダルで選ぶと hidden 項目が追加・削除されるだけで change が起きないため、変化を監視する
  document.querySelectorAll('form').forEach(function(form) {
    if (!form.querySelector(REQUIRED_FOOTER_SELECTOR)) return;

    new MutationObserver(function() { refreshRequiredErrors(form); }).observe(form, { childList: true, subtree: true });
  });

  scrollToFirstError(document);
});
